/**
 * NewGoodsDetailsCom — "Equipment & Cargo Details" modal (rendered in the
 * centered Dialog from ShipmentRateForm).
 *
 *  • Two tabs: Equipment Details (container rows) + Other Details (No. of B/L).
 *  • Rows start EMPTY (no dummy data); "+" adds another empty row.
 *  • A container type can be picked only once — already-used types are disabled
 *    in the other rows' Load Type dropdowns.
 *  • Weight type dropdown (KG/UNIT | TOTAL, saved as `weightType` "unit" |
 *    "total") works like air's: only the first row's is editable and it
 *    applies to every row. Nothing is pre-selected — the user must pick one.
 *  • Apply validates the equipment rows (type/quantity/weight/weight type
 *    required): shows an in-modal red banner (Equipment tab only) + per-field
 *    "… is required" messages; auto-switches to the Equipment tab. No. of B/L
 *    is optional.
 *  • On Apply it writes GoodDetails (rich draft, typed weight) +
 *    containerDetails ({ type, quantity, kgs, weightunit: "KG", weightType },
 *    kgs = line total) back to the parent Formik. Button reads "Apply".
 */
import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { message } from "antd";
import "./NewGoodsDetailsCom.css";
import {
  OCEAN_WEIGHT_TYPE_OPTIONS,
  OCEAN_WEIGHT_TYPE_TOTAL,
  getWeightType,
  roundOceanWeight,
  toPayloadContainer,
} from "../../../../utils/oceanContainerWeight";

const AMBER = "#FFAF00";
const AMBER_HOVER = "#E09800";

const container_type = [
  { value: "20 DV", label: "20 DV" },
  { value: "20 RF", label: "20 RF" },
  { value: "20 FR", label: "20 FR" },
  { value: "40 DV", label: "40 DV" },
  { value: "40 HC", label: "40 HC" },
  { value: "40 RF", label: "40 RF" },
  { value: "40 FR", label: "40 FR" },
  { value: "20 OT", label: "20 OT" },
  { value: "40 OT", label: "40 OT" },
  { value: "45 DV", label: "45 DV" },
  { value: "45 HC", label: "45 HC" },
  { value: "45 RF", label: "45 RF" },
  { value: "45 OT", label: "45 OT" },
  { value: "20 GP", label: "20 GP" },
  { value: "40 GP", label: "40 GP" },
  { value: "20 ISO", label: "20 ISO" },
  { value: "40 ISO", label: "40 ISO" },
  { value: "20 NOR", label: "20 NOR" },
  { value: "40 NOR", label: "40 NOR" },
  { value: "20 HC", label: "20 HC" },
];

// A freshly-added row starts empty. The weight type has no default — it is
// only carried over from the first row once the user has picked one.
const makeRow = (weightType = "") => ({
  type: "",
  quantity: "",
  kgs: "",
  weightType,
});

const NewGoodsDetailsCom = ({ formikData, setAnchorEl, allshipports }) => {
  console.log("formikdata", formikData?.values);
  console.log("allshipports", allshipports);

  // Seed the modal from whatever is already applied so re-opening keeps state.
  const existing = formikData?.values?.GoodDetails;

  const [activeTab, setActiveTab] = useState("equipment");
  const [rows, setRows] = useState(
    Array.isArray(existing?.containers) && existing.containers.length
      ? existing.containers.map((c) => ({
          type: c?.type ?? "",
          quantity: c?.quantity != null ? String(c.quantity) : "",
          kgs: c?.kgs != null ? String(c.kgs) : "",
          // Edit Query rows arrive via toModalRow (ShipmentRateForm), which
          // already resolves the type; getWeightType also reads older shapes.
          weightType: getWeightType(c) || "",
        }))
      : [makeRow()]
  );
  // Number of B/L, persisted to Formik. Every ocean shipment has at least one
  // master B/L, so a missing, blank (older query records saved it as "") OR
  // non-positive stored value opens the field on 1 — the same numeric check
  // Apply/blur below enforces, so the field never opens on something the
  // modal would refuse to save.
  const [blCount, setBlCount] = useState(() =>
    String(Number(existing?.noOfBL) > 0 ? Number(existing.noOfBL) : 1)
  );
  // True after a failed Apply so empty container fields show inline errors.
  const [attempted, setAttempted] = useState(false);

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  // Like air's "Select wt unit", the weight mode is shared by every row: only
  // the first row's dropdown is editable and a change applies to all rows.
  const updateWeightType = (value) =>
    setRows((prev) => prev.map((row) => ({ ...row, weightType: value })));

  const addRow = () =>
    setRows((prev) => [...prev, makeRow(prev[0]?.weightType)]);

  const removeRow = (index) =>
    setRows((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  const onConfirm = () => {
    const incomplete = rows.some(
      (r) => !r.type || !r.quantity || !r.kgs || !r.weightType
    );
    // A weight that rounds to 0 KG (e.g. 0.4) would be searched as nothing.
    const zeroWeight = rows.some((r) => r.kgs && roundOceanWeight(r.kgs) < 1);
    if (incomplete || zeroWeight) {
      setAttempted(true); // shows the in-modal banner + per-field errors
      setActiveTab("equipment");
      return;
    }

    // Rich draft (includes new dummy fields) — saved under GoodDetails with
    // the weight exactly as typed (e.g. 200.60), so re-opening shows it.
    // Only the payload below is rounded to whole KG.
    const containers = rows.map((r) => ({
      type: r.type,
      quantity: String(r.quantity),
      kgs: String(r.kgs),
      weightType: r.weightType,
    }));

    // Commodity type and stuffing now live on the main form (Goods Type /
    // Charges & Terms), so the modal only owns containers and No. of B/L.
    // Clearing the field (or typing 0) still applies as 1 — the payload never
    // carries a blank/zero B/L count.
    const values = {
      containers,
      noOfBL: String(Number(blCount) > 0 ? Number(blCount) : 1),
    };

    if (formikData) {
      formikData.setFieldValue("GoodDetails", values);
      // Final payload container list is
      // { type, quantity, kgs, enteredKgs, weightunit: "KG", weightType }:
      // kgs is the whole-KG line total ("unit" → round(typed) × quantity,
      // "total" → round(typed)) and enteredKgs is the weight as typed, so
      // Edit Query can show 200.60 again (see utils/oceanContainerWeight).
      formikData.setFieldValue(
        "containerDetails",
        containers.map(toPayloadContainer)
      );
      setAnchorEl && setAnchorEl(null);
      message.success("Goods details successfully saved");
    } else {
      console.log("Final submitted values:", values);
      message.success(
        "Form Submitted! (Note: setFormikData prop is missing to save data externally.)"
      );
    }
  };

  const tabSx = (active) => ({
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12.5,
    borderRadius: "999px",
    px: 2,
    py: 0.75,
    minWidth: 0,
    boxShadow: "none",
    ...(active
      ? {
          background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
          color: "#fff",
          "&:hover": {
            background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
          },
        }
      : {
          background: "#fff",
          color: "#4b5563",
          border: "1px solid #d8dce3",
          "&:hover": { borderColor: AMBER_HOVER, background: "#fff" },
        }),
  });

  const fieldSx = { "& .MuiInputBase-root": { fontSize: 13, background: "#fff" } };

  // Types already chosen in any row — used to disable them in the other rows
  // so a container type can be selected only once.
  const usedTypes = rows.map((r) => r.type).filter(Boolean);

  // After a failed Apply, show the banner while any equipment field is empty.
  const hasIncomplete = rows.some(
    (r) =>
      !r.type ||
      !r.quantity ||
      !r.kgs ||
      roundOceanWeight(r.kgs) < 1 ||
      !r.weightType
  );
  const showError = attempted && hasIncomplete;

  return (
    <Box className="eqm-root">
      {/* Header */}
      <div className="eqm-header">
        <span className="eqm-icon-box">
          <LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />
        </span>
        <h2>Equipment &amp; Cargo Details</h2>
      </div>

      {/* Tabs */}
      <div className="eqm-tabs">
        <Button
          type="button"
          variant={activeTab === "equipment" ? "contained" : "outlined"}
          sx={tabSx(activeTab === "equipment")}
          onClick={() => setActiveTab("equipment")}
        >
          Equipment Details
        </Button>
        <Button
          type="button"
          variant={activeTab === "other" ? "contained" : "outlined"}
          sx={tabSx(activeTab === "other")}
          onClick={() => setActiveTab("other")}
        >
          Other Details
        </Button>
      </div>

      <hr className="eqm-divider" />

      {showError && activeTab === "equipment" && (
        <div className="eqm-error-banner">
          Please fill all equipment details — they cannot be empty.
        </div>
      )}

      {/* Body */}
      <div className="eqm-body">
        {activeTab === "equipment" ? (
          <div>
            {/* Column headers */}
            <div className="eqm-grid eqm-head-row">
              <span className="eqm-head-label">Load Type</span>
              <span className="eqm-head-label">Quantity</span>
              <span className="eqm-head-label">Container Weight</span>
              <span />
            </div>

            {rows.map((row, index) => (
              <div className="eqm-grid" key={index}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  error={attempted && !row.type}
                  helperText={
                    attempted && !row.type ? "Load type is required" : ""
                  }
                  value={row.type}
                  onChange={(e) => updateRow(index, "type", e.target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (val) =>
                      val ? (
                        val
                      ) : (
                        <span style={{ color: "#9aa2ad" }}>Load Type</span>
                      ),
                  }}
                  sx={fieldSx}
                >
                  {container_type.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                      disabled={
                        opt.value !== row.type && usedTypes.includes(opt.value)
                      }
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  placeholder="Quantity"
                  error={attempted && !row.quantity}
                  helperText={
                    attempted && !row.quantity ? "Quantity is required" : ""
                  }
                  value={row.quantity}
                  onChange={(e) => updateRow(index, "quantity", e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={fieldSx}
                />

                <div className="eqm-weight-cell">
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    placeholder={
                      row.weightType === OCEAN_WEIGHT_TYPE_TOTAL
                        ? "Total Weight"
                        : row.weightType
                        ? "Weight per Container"
                        : "Container Weight"
                    }
                    error={
                      attempted && (!row.kgs || roundOceanWeight(row.kgs) < 1)
                    }
                    helperText={
                      !attempted
                        ? ""
                        : !row.kgs
                        ? "Container weight is required"
                        : roundOceanWeight(row.kgs) < 1
                        ? "Weight must be at least 1 KG"
                        : ""
                    }
                    value={row.kgs}
                    onChange={(e) => updateRow(index, "kgs", e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={fieldSx}
                  />
                  <TextField
                    select
                    size="small"
                    title="Select weight unit"
                    value={rows[0]?.weightType || ""}
                    disabled={index !== 0}
                    error={attempted && !rows[0]?.weightType}
                    helperText={
                      index === 0 && attempted && !rows[0]?.weightType
                        ? "Weight unit is required"
                        : ""
                    }
                    onChange={(e) => updateWeightType(e.target.value)}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (val) =>
                        OCEAN_WEIGHT_TYPE_OPTIONS.find((o) => o.value === val)
                          ?.label || (
                          <span style={{ color: "#9aa2ad" }}>Select wt unit</span>
                        ),
                    }}
                    sx={{ ...fieldSx, width: 118, flexShrink: 0 }}
                  >
                    {OCEAN_WEIGHT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                <div className="eqm-actions">
                  <IconButton
                    size="small"
                    onClick={() => removeRow(index)}
                    disabled={rows.length <= 1}
                    sx={{
                      background: "#fbe1dc",
                      color: "#c4463a",
                      borderRadius: "8px",
                      "&:hover": { background: "#f6d2cb" },
                      "&.Mui-disabled": { background: "#f4f5f7", color: "#c9cdd4" },
                    }}
                    title="Remove row"
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  {index === rows.length - 1 && (
                    <IconButton
                      size="small"
                      onClick={addRow}
                      sx={{
                        background: "#fff4e0",
                        color: AMBER_HOVER,
                        borderRadius: "8px",
                        "&:hover": { background: "#ffe9c4" },
                      }}
                      title="Add row"
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ maxWidth: 220 }}>
            <label className="eqm-head-label">No. of B/L</label>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={blCount}
              onChange={(e) => setBlCount(e.target.value)}
              // Leaving the field blank/0 snaps it back to the 1 minimum, so
              // what's shown matches what Apply will store.
              onBlur={() =>
                setBlCount((v) => (Number(v) > 0 ? String(Number(v)) : "1"))
              }
              inputProps={{ min: 1 }}
              sx={{ ...fieldSx, mt: 0.5 }}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="eqm-footer">
        <Button
          type="button"
          variant="outlined"
          onClick={() => setAnchorEl && setAnchorEl(null)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderColor: "#d8dce3",
            color: "#4b5563",
            borderRadius: "8px",
            px: 2.5,
            "&:hover": { borderColor: AMBER_HOVER, color: AMBER_HOVER },
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={onConfirm}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            px: 3,
            background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
            "&:hover": {
              background: `linear-gradient(135deg, ${AMBER}, ${AMBER_HOVER})`,
              filter: "brightness(1.03)",
            },
          }}
        >
          Apply
        </Button>
      </div>
    </Box>
  );
};

export default NewGoodsDetailsCom;
